import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { PmnLogo } from "@/components/PmnLogo";
import { trackEvent, EVENTS } from "@/lib/analytics";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/services", label: "Services" },
  { to: "/capabilities", label: "Capabilities" },
  { to: "/case-studies", label: "Case Studies" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const solid = scrolled || open;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid ? "bg-paper/90 backdrop-blur-md border-b border-charcoal/10" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex max-w-8xl items-center justify-between gap-3 px-6 py-5 lg:px-10">
        <Link to="/" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="nav-logo">
          <PmnLogo size={26} tone={solid ? "#1F2024" : "#F5F3EE"} className="shrink-0" />
          <span
            className={`font-serif text-[0.95rem] leading-tight tracking-wideish sm:text-base lg:text-lg ${
              solid ? "text-charcoal" : "text-paper"
            }`}
          >
            Project Management Network
          </span>
        </Link>

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `link-underline text-sm tracking-wideish transition-colors ${
                  solid ? "text-charcoal/80 hover:text-teal" : "text-paper/80 hover:text-paper"
                } ${isActive ? (solid ? "!text-teal" : "!text-paper") : ""}`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-cta"
            onClick={() => trackEvent(EVENTS.ctaPrimary, { cta_label: "Discuss an Initiative", cta_location: "navbar" })}
            className="bg-teal px-5 py-2.5 text-sm tracking-wideish text-paper transition-colors duration-500 hover:bg-charcoal"
          >
            Discuss an Initiative
          </Link>
        </div>

        <button
          className={`shrink-0 lg:hidden ${solid ? "text-charcoal" : "text-paper"}`}
          onClick={() => setOpen((v) => !v)}
          data-testid="nav-mobile-toggle"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="mobile-nav"
        >
          {open ? <X strokeWidth={1.5} aria-hidden="true" /> : <Menu strokeWidth={1.5} aria-hidden="true" />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            id="mobile-nav"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden bg-paper lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-4">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="border-b border-charcoal/10 py-3 text-charcoal/80"
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
